import { Observable } from 'rxjs';

import { AuthSession } from '../../models/auth-session.model';
import {
  FastLoginChallengeDto,
  FastLoginRequestBody,
  FastLoginResendBody,
  FastLoginVerifyBody,
} from '../../models/fast-login.dto';
import { LoginRequest, SocialLoginRequest } from '../../models/login.dto';
import {
  RequestPasswordResetRequest,
  ResetPasswordRequest,
  VerifyOtpRequest,
  VerifyOtpResponseDto,
} from '../../models/password-reset.dto';
import {
  RegisterAccountRequest,
  RegisterCompleteRequest,
} from '../../models/register.dto';

/**
 * Abstract class rather than an interface so it can double as the DI token —
 * pages bind it to the implementation with
 * `{ provide: AuthRepositoryContract, useClass: AuthRepository }`.
 */
export abstract class AuthRepositoryContract {
  abstract login(body: LoginRequest): Observable<AuthSession>;
  abstract socialLogin(body: SocialLoginRequest): Observable<AuthSession>;
  /** Registration step 1; resolves to the registrationId for step 2. */
  abstract registerAccount(body: RegisterAccountRequest): Observable<string>;
  /** Registration step 2; the account exists and is signed in after this. */
  abstract registerComplete(body: RegisterCompleteRequest): Observable<AuthSession>;
  /** Invalidates the session server-side; resolves to the API's confirmation line. */
  abstract logout(): Observable<string | undefined>;

  /** Emails a code; resolves to the API's confirmation line, if it sent one. */
  abstract requestPasswordReset(body: RequestPasswordResetRequest): Observable<string | undefined>;
  /** Emails a fresh code for the same address. */
  abstract resendOtp(body: RequestPasswordResetRequest): Observable<string | undefined>;
  abstract verifyOtp(body: VerifyOtpRequest): Observable<VerifyOtpResponseDto>;
  abstract resetPassword(body: ResetPasswordRequest): Observable<void>;

  /** Fast login: request OTP via email or WhatsApp. */
  abstract fastLoginRequest(body: FastLoginRequestBody): Observable<FastLoginChallengeDto>;
  /** Fast login: resend OTP (invalidates previous code). */
  abstract fastLoginResend(body: FastLoginResendBody): Observable<FastLoginChallengeDto>;
  /** Fast login: verify OTP and get session. */
  abstract fastLoginVerify(body: FastLoginVerifyBody): Observable<AuthSession>;
}
